import { Component, Input, OnChanges, OnDestroy, ViewChild, inject } from '@angular/core'
import { KeyingStructureService } from '../../../services/keying.structure.service'
import { StructureService, IStructure, JsonPatchOperation } from '../../../services/structure/structure.service'
import { AttachmentsService, CreateAttachmentRequest } from '../../../services/attachments/attachments.service'
import { MatTableDataSource } from '@angular/material/table'
import { Subject, take, takeUntil } from 'rxjs'
import { ErrorService } from '../../../services/error.service'
import { ConfirmDialogComponent } from '../../../dialogs/confirm-dialog/confirm-dialog.component'
import { MatPaginator, PageEvent } from '@angular/material/paginator'
import { DialogService } from '../../../services/dialog.service'

/**
 * AddAttachmentsCardComponent
 *
 * A comprehensive file attachment management component for structure entities.
 * Provides full CRUD operations for file attachments with a clean, user-friendly interface.
 *
 * FEATURES:
 * - Upload files (PDF, Excel formats)
 * - Download existing attachments with cross-browser compatibility
 * - Delete attachments with confirmation dialog
 * - Paginated table view with sorting and filtering
 * - Permission-based UI (super users and owners can delete)
 * - Automatic structure document flag management
 *
 * SERVICES INTEGRATION:
 * - AttachmentsService: Core CRUD operations with tokenized loading
 * - StructureService: Structure updates (HasDocuments flag)
 * - KeyingStructureService: Legacy state management (TODO-REFACTOR-STRUCTURE)
 *
 * INPUTS:
 * @Input() structure - The structure entity to manage attachments for
 * @Input() user - Current user for permission checks
 *
 * LIFECYCLE:
 * - OnChanges: Loads attachments when structure changes
 * - OnDestroy: Proper cleanup of subscriptions to prevent memory leaks
 *
 * ACCESSIBILITY:
 * - Material Design components for consistent UX
 * - Keyboard navigation support
 * - Screen reader friendly
 *
 * BROWSER COMPATIBILITY:
 * - Modern browsers: Standard download API
 * - Internet Explorer: msSaveOrOpenBlob fallback
 * - Cross-platform file handling
 */
@Component({
  selector: 'app-add-attachments-card',
  templateUrl: './add-attachments-card.component.html',
  styleUrls: ['./add-attachments-card.component.scss'],
  standalone: false,
})
export class AddAttachmentsCardComponent implements OnChanges, OnDestroy {
  @ViewChild(MatPaginator) paginator: MatPaginator
  pageSizeOptions: number[] = [5, 10, 25]
  @Input() structure: IStructure
  @Input() user
  attachments
  hasAttachments = false
  dataSource: MatTableDataSource<any> = new MatTableDataSource([])
  expanded = true
  pageSize = 5
  currentPage = 0
  private destroy$ = new Subject<boolean>()

  displayedColumns: string[] = ['createdOn', 'createdBy', 'name', 'download', 'delete']
  displayedColumnFilters: string[] = [
    'createdOn-filter',
    'createdBy-filter',
    'name-filter',
    'download-filter',
    'delete-filter',
  ]

  constructor(
    private keyingStructureService: KeyingStructureService,
    private structureService: StructureService,
    private attachmentsService: AttachmentsService,
    private dialogService: DialogService,
    private errorService: ErrorService
  ) {}

  ngOnChanges(): void {
    this.getAttachments()
  }

  ngOnDestroy(): void {
    this.destroy$.next(true)
    this.destroy$.complete()
  }

  private processAttachmentsData(data: any[]): void {
    if (data.length > 0) {
      this.attachments = data
      this.setAttachmentsTable()
      this.hasAttachments = true
    } else {
      this.attachments = []
      this.hasAttachments = false
      this.resetTableAndPaginator()
    }
  }

  private resetTableAndPaginator(): void {
    this.dataSource.data = []
    if (this.paginator) {
      this.paginator.length = 0
      this.paginator.pageIndex = 0
      this.currentPage = 0
    }
  }

  getAttachments() {
    if (!this.structure?.id) {
      return
    }

    this.attachmentsService
      .getAttachments(this.structure.id)
      .pipe(take(1))
      .subscribe(
        (data) => {
          this.processAttachmentsData(data)
        },
        (error) => {
          this.errorService.throw(error)
        }
      )
  }

  setAttachmentsTable() {
    this.attachments.sort((a, b) => {
      return new Date(b.createdOn).getTime() - new Date(a.createdOn).getTime()
    })

    // Update data source data
    this.dataSource.data = this.attachments

    // Set up paginator if available
    if (this.paginator) {
      this.dataSource.paginator = this.paginator
      this.paginator.pageIndex = this.currentPage
      this.paginator.length = this.attachments.length
    }
  }

  applyFilter(event?: Event) {
    const filterValue = (event.target as HTMLInputElement).value
    this.dataSource.filter = filterValue.trim().toLowerCase()
  }

  pageChanged(event: PageEvent) {
    this.pageSize = event.pageSize
    this.currentPage = event.pageIndex
  }
  uploadAttachment(files) {
    if (!files || files.length === 0) {
      return
    }

    const file = files[0]
    const uploadRequest: CreateAttachmentRequest = {
      fileName: file.name,
      file: file,
      description: `Uploaded by ${this.user?.name || 'User'}`,
    }

    this.attachmentsService
      .uploadAttachment(uploadRequest, this.structure.id)
      .pipe(take(1))
      .subscribe(
        () => {
          this.clearFileInput()
          if (this.attachments.length < 1) {
            this.updateStructure(true)
          } else {
            this.getAttachments()
          }
        },
        (error) => {
          this.errorService.throw(error)
        }
      )
  }

  private clearFileInput(): void {
    const fileInput = document.getElementById('file-upload') as HTMLInputElement
    if (fileInput) {
      fileInput.value = ''
    }
  }
  updateStructure(hasDocuments: boolean) {
    const request: JsonPatchOperation[] = [
      {
        op: 'replace',
        path: '/HasDocuments',
        value: hasDocuments,
      },
    ]

    this.structureService
      .patchStructure(this.structure.id, request)
      .pipe(take(1))
      .subscribe(
        (structure) => {
          // TODO-REFACTOR-STRUCTURE: Replace with new StructureService state management
          // Temporary cast until legacy KeyingStructureService is refactored
          this.keyingStructureService.selectStructure(structure as any)
          this.errorService.confirm('Updated Structure')
        },
        (error) => {
          this.errorService.throw(error)
        }
      )
  }

  private saveFileForIE(blob: Blob): void {
    if (window.navigator && (window.navigator as any).msSaveOrOpenBlob) {
      ;(window.navigator as any).msSaveOrOpenBlob(blob)
    }
  }

  private saveFileForModernBrowsers(blob: Blob, filename: string): void {
    const data = window.URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = data
    link.download = filename
    link.dispatchEvent(new MouseEvent('click', { bubbles: true, cancelable: true, view: window }))

    setTimeout(() => {
      window.URL.revokeObjectURL(data)
      link.remove()
    }, 100)
  }

  private triggerFileDownload(file: Blob, attachment: any): void {
    const newBlob = new Blob([file], { type: file.type })
    const filename = attachment.name || `${this.structure.id}.attachment`

    if (window.navigator && (window.navigator as any).msSaveOrOpenBlob) {
      this.saveFileForIE(newBlob)
      return
    }

    this.saveFileForModernBrowsers(newBlob, filename)
  }

  downloadAttachment(attachment) {
    this.attachmentsService
      .downloadAttachment(attachment.id, this.structure.id)
      .pipe(take(1))
      .subscribe({
        next: (file) => {
          this.triggerFileDownload(file, attachment)
        },
        error: (error) => {
          this.errorService.throw(error)
        },
      })
  }

  private getDeleteConfirmData(attachment) {
    return {
      title: 'Delete Attachment',
      message: `Are you sure you want to delete this attachment created by ${attachment.createdBy}?`,
      cancel: 'Cancel',
      confirm: 'Delete',
      showCancel: true,
      reasonRequired: false,
    }
  }

  private handlePostDeleteActions() {
    if (this.attachments.length === 1) {
      this.updateStructure(false)
    }
    this.getAttachments()
  }

  deleteAttachment(attachment) {
    const confirmData = this.getDeleteConfirmData(attachment)
    const dialogRef = this.dialogService.open(ConfirmDialogComponent, confirmData, false)

    dialogRef
      .afterClosed()
      .pipe(takeUntil(this.destroy$))
      .subscribe((result) => {
        if (result.confirmed) {
          this.attachmentsService
            .deleteAttachment(attachment.id, this.structure.id)
            .pipe(take(1))
            .subscribe(
              () => {
                this.handlePostDeleteActions()
              },
              (error) => {
                this.errorService.throw(error)
              }
            )
        }
      })
  }

  isDeleteDisabled(attachment: any): boolean {
    if (!this.user) {
      return true
    }

    return !(this.user.isSuperUser || this.user.id === attachment.createdById)
  }
}
